// src/components/WaterLevelTable.js
// ═══════════════════════════════════════════════════════════════════════════
// Enterprise-Grade Water Level Monitoring Table — Full Merged Edition
// With Bi-Directional Highlighting
// ═══════════════════════════════════════════════════════════════════════════
// Core Features:
//   - Animated level bars with shimmer effects
//   - Real-time trend indicators with historical comparison
//   - Comprehensive history drawer with timeline visualization
//   - Per-sensor alert muting and management
//   - Advanced search/filter with multi-criteria support
//   - Sortable columns with visual indicators
//   - CSV export with configurable fields
//   - Pipeline linking/unlinking with validation
//   - Skeleton loading states and empty states
//
// Enhanced Features:
//   - Online/Offline System Awareness and Tabs (with strict TTL)
//   - Advanced debugging system with performance tracking
//   - Data quality indicators and validation
//   - Batch operations (multi-select, bulk actions)
//   - Anomaly detection and highlighting
//   - Auto-refresh indicators
//   - Enhanced error handling and recovery
//   - Performance metrics dashboard
//   - Export to multiple formats (CSV, JSON)
//   - Customizable column visibility
//   - Advanced filtering (date range, custom predicates)
//
// V2 Additions (Merged):
//   - Polymorphic entity linking (Pipeline OR Asset/Well/Tank)
//   - onLinkAsset prop for asset binding
//   - onSensorSelect prop for detail view
//   - Polymorphic entity cell (Linked Entity column)
//   - Polymorphic action menu (MdMoreVert context menu)
//   - Asset tag display (teal) vs Pipeline tag (purple)
//
// V3 Additions (Merged):
//   - Bi-directional highlighting via selectedSensorId prop
//   - Clickable rows (row click → onSensorSelect)
//   - Highlighted row: tinted background + cyan left border
//   - Checkbox / Actions cells stop click propagation
//   - Checkbox state (isChecked) separated from highlight state (isSelected)
//
// --- UPDATE: V4 Additions
//   - Quick-filter chips (Alerts / AI flagged / Unassigned / Muted) with live counts
//   - Clickable summary cards filter by level; "No Data" card
//   - Column picker (visibleColumns is now user-controllable) + compact density
//   - Preferences + muted sensors persisted to localStorage (safe fallback)
//   - Expandable row details: level history strip, data-quality issues, AI forecast
//   - History drawer analytics: step chart, time-in-level, transitions, history CSV
//   - Bulk mute / unmute / export selected / refresh history; confirm before bulk unlink
//   - Export scope (all / filtered / selected); CSV fields are now properly quoted
//   - AI flood-risk sort; search also matches linked entity IDs
//   - Relative "last seen" ages that tick every 30s; online/offline TTL re-evaluated on the same tick
//   - Incremental rendering (Load more) and capped row animations for large fleets
//   - Keyboard: "/" focuses search, Esc clears selection & expanded rows
//   - Click-to-copy device IDs; debug button only when the debugger is enabled
// --- END UPDATE
//
// --- UPDATE: V5 — live status, sticky rows, FlowSense AI, performance
//   - Rows come from a sticky registry: sensors ∪ devices sending readings, matched
//     case-insensitively; a sensor missing from one list refresh stays (SYNCING) instead
//     of disappearing; devices sending readings that aren't registered show NOT IN REGISTER
//   - Live state per sensor: Transmitting / Online / Delayed / Offline / No data, from when
//     data was last heard (arrival in this browser, reading time, sensor last_activity_at).
//     The WebSocket state is a banner and no longer marks every sensor offline
//   - A reading replayed from a node's buffer counts as heard now; an older REST reading
//     can't replace a newer live one; last known level stays visible when offline
//   - FlowSense AI: flood forecast (page predictions or sensor.ai_predictions), asset
//     health, and an edge anomaly check (rapid rise / fall, flapping) on real level history;
//     AI fleet strip; "AI flagged" filter and AI sort use all of them
//   - Performance: memoised WaterLevelRow, stable handlers, shared 5 s clock, no infinite
//     per-row animations (shimmer / pulse removed), lazy menus, auto-refresh limited to
//     visible rows every 15 s
// --- END UPDATE
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
// --- UPDATE: V5 — unused imports removed (Spinner, Stat*, Alert*, Popover*, Switch, FormControl,
// FormLabel, Collapse, Portal, TabPanels, TabPanel, ButtonGroup, MdSpeed, MdSettings, MdVisibilityOff)
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Badge, Text, HStack,
  VStack, Icon, Tooltip, IconButton, useColorModeValue, Flex,
  Input, InputGroup, InputLeftElement, Select, Button,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent,
  DrawerCloseButton, useDisclosure, Tag, TagLabel, TagLeftIcon,
  SimpleGrid, Skeleton, useToast, Checkbox, Menu,
  MenuButton, MenuList, MenuItem, Divider, Progress, Code,
  Kbd, Tabs, TabList, Tab,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, MenuOptionGroup, MenuItemOption,
  MenuDivider, MenuGroup, Wrap, WrapItem,
} from '@chakra-ui/react';
// --- END UPDATE
import {
  MdHistory, MdLink, MdLinkOff, MdWaves, MdSearch,
  MdDownload, MdCheckCircle, MdWarning, MdError, MdInfo,
  MdTrendingUp, MdTrendingDown, MdTrendingFlat, MdWaterDrop,
  MdOutlineWaterDrop, MdSensors, MdSensorsOff, MdNotifications,
  MdNotificationsOff, MdSort, MdClose, MdRefresh, MdBugReport,
  MdVisibility, MdFilterList,
  MdSelectAll, MdMoreVert, MdFileDownload, MdDelete, MdAutorenew,
  // --- UPDATE
  MdViewColumn, MdFormatLineSpacing, MdExpandMore, MdExpandLess,
  MdContentCopy, MdAutoAwesome, MdNotificationsActive, MdTimeline,
  MdLinkOff as MdUnassigned,
  // --- END UPDATE
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';

// =========================================================================
// ENTERPRISE FIX: IMPORT GLOBAL WEBSOCKET CONTEXT
// =========================================================================
import { useNotifications } from '../contexts/NotificationContext';
// --- UPDATE: V5 shared live-status + AI modules
import { useLiveSensorRegistry, useNow } from './sensors/useLiveSensorRegistry';
import { LIVE_STATE_META, formatAgo, normalizeId } from './sensors/sensorLiveStatus';
import { deriveSensorAi } from './sensors/sensorAiInsights';
import { AiFleetStrip, LiveFeedBanner, LiveStatusIndicator, SensorAiBadge, SensorAiPanel, summarizeAi } from './sensors/SensorLiveUi';
// --- END UPDATE

const MotionBox = motion(Box);
// --- UPDATE: V5 — MotionTr removed (rows are plain memoised <Tr>)
// --- END UPDATE

// ═════════════════════════════════════════════════════════════════════════════
// ADVANCED DEBUGGING & PERFORMANCE MONITORING
// ═════════════════════════════════════════════════════════════════════════════

class WaterLevelDebugger {
  constructor() {
    this.enabled = process.env.NODE_ENV === 'development';
    this.logs = [];
    this.maxLogs = 200;
    this.metrics = {
      renders: 0,
      dataUpdates: 0,
      filterOperations: 0,
      sortOperations: 0,
      apiCalls: 0,
      errors: 0,
    };
    this.performanceMarks = new Map();
  }

  log(level, message, data = null) {
    if (!this.enabled) return;
    const entry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      component: 'WaterLevelTable',
    };
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) this.logs.shift();

    const style = {
      debug:   'color: #888',
      info:    'color: #2196F3; font-weight: bold',
      warn:    'color: #FF9800; font-weight: bold',
      error:   'color: #F44336; font-weight: bold',
      success: 'color: #4CAF50; font-weight: bold',
    }[level] || 'color: #2196F3';

    console.log(`%c[WaterLevelTable][${level.toUpperCase()}] ${message}`, style, data || '');
  }

  startMark(label) {
    if (!this.enabled) return;
    this.performanceMarks.set(label, performance.now());
  }

  endMark(label) {
    if (!this.enabled) return 0;
    const start = this.performanceMarks.get(label);
    if (!start) return 0;
    const duration = performance.now() - start;
    this.performanceMarks.delete(label);
    this.log('debug', `Performance: ${label}`, { duration: `${duration.toFixed(2)}ms` });
    return duration;
  }

  incrementMetric(key) {
    if (this.metrics[key] !== undefined) this.metrics[key]++;
  }

  getMetrics() {
    return { ...this.metrics };
  }

  getLogs(level = null) {
    return level ? this.logs.filter(l => l.level === level) : [...this.logs];
  }

  clear() {
    this.logs = [];
    Object.keys(this.metrics).forEach(k => this.metrics[k] = 0);
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// DATA VALIDATION & QUALITY ASSESSMENT
// ═════════════════════════════════════════════════════════════════════════════

class DataQualityValidator {
  static validateSensor(sensor) {
    const issues = [];
    if (!sensor) return { isValid: false, issues: ['Sensor is null'], score: 0 };
    if (!sensor.device_id) issues.push('Missing device_id');
    if (!sensor.type || sensor.type !== 'water_level') issues.push('Invalid or missing type');
    return {
      isValid: issues.length === 0,
      issues,
      score: Math.max(0, 100 - (issues.length * 25)),
    };
  }

  static validateReading(reading) {
    const issues = [];
    if (!reading) return { isValid: false, issues: ['Reading is null'], score: 0 };
    if (!reading.level) issues.push('Missing level');
    if (!['HIGH', 'MEDIUM', 'LOW', 'EMPTY'].includes(reading.level)) issues.push('Invalid level value');
    if (!reading.timestamp) issues.push('Missing timestamp');
    else {
      const age = Date.now() - new Date(reading.timestamp).getTime();
      if (age > 300000) issues.push('Stale data (>5 min)');
    }
    return {
      isValid: issues.length === 0,
      issues,
      score: Math.max(0, 100 - (issues.length * 20)),
    };
  }

  static assessOverallQuality(sensors, readings) {
    let totalScore = 0;
    let count = 0;

    sensors.forEach(s => {
      const sensorQuality  = this.validateSensor(s);
      const reading        = readings[s.device_id];
      const readingQuality = reading ? this.validateReading(reading) : { score: 0 };
      totalScore += (sensorQuality.score + readingQuality.score) / 2;
      count++;
    });

    return count > 0 ? Math.round(totalScore / count) : 0;
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// LEVEL CONFIGURATION
// ═════════════════════════════════════════════════════════════════════════════

const getLevelConfig = (level) => {
  const map = {
    HIGH:   { color: 'cyan',   value: 100, label: 'High',   icon: MdWaves,            severity: 'info',    grad: 'linear(to-r, cyan.400, blue.500)'    },
    MEDIUM: { color: 'blue',   value: 60,  label: 'Medium', icon: MdOutlineWaterDrop, severity: 'normal',  grad: 'linear(to-r, blue.400, blue.600)'    },
    LOW:    { color: 'orange', value: 30,  label: 'Low',    icon: MdOutlineWaterDrop, severity: 'warning', grad: 'linear(to-r, orange.400, orange.600)' },
    EMPTY:  { color: 'red',    value: 5,   label: 'Empty',  icon: MdSensors,          severity: 'error',   grad: 'linear(to-r, red.400, red.600)'      },
  };
  return map[level] || { color: 'gray', value: 0, label: 'Unknown', icon: MdSensors, severity: 'unknown', grad: 'linear(to-r, gray.400, gray.600)' };
};

// ═════════════════════════════════════════════════════════════════════════════
// --- UPDATE: V4 SHARED HELPERS
// ═════════════════════════════════════════════════════════════════════════════

const LEVEL_ORDER         = { EMPTY: 0, LOW: 1, MEDIUM: 2, HIGH: 3 };
const ALERT_LEVELS        = new Set(['HIGH', 'LOW', 'EMPTY']);
// --- UPDATE: V5 — AI_FLAG_THRESHOLD now lives in ./sensors/sensorAiInsights (same 0.65)
// --- END UPDATE
// --- UPDATE: V5 — shared 5 s live clock (was a 30 s per-table interval)
const LIVE_CLOCK_MS       = 5000;
const AUTO_REFRESH_MS     = 15000;
// --- END UPDATE
const DEFAULT_PAGE_SIZE   = 50;
const DEFAULT_COLUMNS     = Object.freeze({
  status: true, id: true, level: true, fill: true, trend: true,
  alert: true, pipeline: true, lastSeen: true, actions: true,
});
const COLUMN_LABELS = [
  { key: 'status',   label: 'Live status' },
  { key: 'id',       label: 'Sensor' },
  { key: 'level',    label: 'Level' },
  { key: 'fill',     label: 'Fill status' },
  { key: 'trend',    label: 'Trend' },
  { key: 'alert',    label: 'Alert & FlowSense AI' },
  { key: 'pipeline', label: 'Linked entity' },
  { key: 'lastSeen', label: 'Last seen' },
  { key: 'actions',  label: 'Actions' },
];

const loadPrefs = (key) => {
  try {
    if (typeof window === 'undefined' || !key) return {};
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const savePrefs = (key, value) => {
  try {
    if (typeof window !== 'undefined' && key) window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode / quota) — preferences simply aren't persisted */
  }
};

// --- UPDATE: V5 — formatAge replaced by formatAgo (./sensors/sensorLiveStatus)
// --- END UPDATE

const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.round(h / 24)}d`;
};

const csvEscape = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadBlob = (content, type, filename) => {
  const blob = new Blob([content], { type });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const getSensorAi = (sensor, aiPredictions) => {
  const linkedAssetId = sensor.linked_entity_id || sensor.asset_id;
  return aiPredictions ? aiPredictions[String(linkedAssetId)] || null : null;
};

const getLinkInfo = (sensor, links) => {
  const isAssetLink = sensor.linked_entity_type === 'asset' && !!sensor.linked_entity_id;
  const isPipeLink  = !!links[sensor.device_id] || sensor.linked_entity_type === 'pipeline';
  return { isAssetLink, isPipeLink, isUnassigned: !isAssetLink && !isPipeLink };
};

// ═════════════════════════════════════════════════════════════════════════════
// --- UPDATE: LEVEL HISTORY STRIP (compact per-reading colour blocks, oldest → newest)
// ═════════════════════════════════════════════════════════════════════════════

const LevelHistoryStrip = React.memo(({ history = [], max = 24, height = '14px' }) => {
  const items = useMemo(() => (history || []).slice(0, max).reverse(), [history, max]);
  if (!items.length) return <Text fontSize="10px" color="gray.400">No history yet</Text>;
  return (
    <HStack spacing="2px" align="stretch" role="img" aria-label={`Last ${items.length} level readings`}>
      {items.map((e, i) => {
        const cfg = getLevelConfig(e.level);
        return (
          <Tooltip
            key={`${e.timestamp || i}-${i}`}
            hasArrow
            openDelay={120}
            label={`${cfg.label} · ${e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}`}
          >
            <Box w="8px" h={height} borderRadius="2px" bg={`${cfg.color}.400`} opacity={0.35 + (0.65 * (i + 1)) / items.length} />
          </Tooltip>
        );
      })}
    </HStack>
  );
});
LevelHistoryStrip.displayName = 'LevelHistoryStrip';

// ═════════════════════════════════════════════════════════════════════════════
// --- UPDATE: HISTORY ANALYTICS (step chart + time-in-level + transitions)
// ═════════════════════════════════════════════════════════════════════════════

const computeHistoryStats = (history = []) => {
  const chronological = [...(history || [])]
    .filter((e) => e && e.level)
    .reverse(); // history[0] is newest
  const timeIn  = { HIGH: 0, MEDIUM: 0, LOW: 0, EMPTY: 0 };
  const counts  = { HIGH: 0, MEDIUM: 0, LOW: 0, EMPTY: 0 };
  let transitions = 0;
  chronological.forEach((e, i) => {
    if (counts[e.level] !== undefined) counts[e.level] += 1;
    const next = chronological[i + 1];
    if (next) {
      if (next.level !== e.level) transitions += 1;
      const dt = new Date(next.timestamp).getTime() - new Date(e.timestamp).getTime();
      if (Number.isFinite(dt) && dt > 0 && timeIn[e.level] !== undefined) timeIn[e.level] += dt;
    }
  });
  const first = chronological[0]?.timestamp ? new Date(chronological[0].timestamp).getTime() : null;
  const last  = chronological.length ? new Date(chronological[chronological.length - 1].timestamp).getTime() : null;
  return {
    chronological,
    counts,
    timeIn,
    transitions,
    spanMs: first && last ? last - first : 0,
    totalTimed: Object.values(timeIn).reduce((a, b) => a + b, 0),
  };
};

const LevelStepChart = React.memo(({ chronological = [], width = 400, height = 90 }) => {
  const geometry = useMemo(() => {
    if (chronological.length < 2) return null;
    const pad = 8;
    const w = width - pad * 2;
    const h = height - pad * 2;
    const step = w / (chronological.length - 1);
    const yOf = (lvl) => pad + (1 - (LEVEL_ORDER[lvl] ?? 0) / 3) * h;
    let d = '';
    chronological.forEach((e, i) => {
      const x = pad + i * step;
      const y = yOf(e.level);
      if (i === 0) d += `M${x.toFixed(1)},${y.toFixed(1)}`;
      else d += ` H${x.toFixed(1)} V${y.toFixed(1)}`;
    });
    const dots = chronological.map((e, i) => ({ x: pad + i * step, y: yOf(e.level), level: e.level }));
    const gridY = [3, 2, 1, 0].map((v) => ({ v, y: pad + (1 - v / 3) * h }));
    return { d, dots, gridY };
  }, [chronological, width, height]);

  if (!geometry) return null;
  const labels = ['EMPTY', 'LOW', 'MEDIUM', 'HIGH'];
  return (
    <Box color="cyan.500">
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Water level step chart" style={{ display: 'block' }}>
        {geometry.gridY.map((g) => (
          <g key={g.v}>
            <line x1={0} x2={width} y1={g.y} y2={g.y} stroke="currentColor" strokeOpacity={0.12} strokeDasharray="3 3" />
            <text x={width - 2} y={g.y - 2} textAnchor="end" fontSize="8" fill="currentColor" fillOpacity={0.55}>
              {labels[g.v]}
            </text>
          </g>
        ))}
        <path d={geometry.d} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
        {geometry.dots.length <= 60 &&
          geometry.dots.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={2.2} fill="currentColor" />)}
      </svg>
    </Box>
  );
});
LevelStepChart.displayName = 'LevelStepChart';
// --- END UPDATE

// ═════════════════════════════════════════════════════════════════════════════
// ANIMATED FILL BAR
// ═════════════════════════════════════════════════════════════════════════════

// --- UPDATE: V5 — CSS width transition; the infinite framer-motion shimmer on every row is gone
const WaterFillBar = React.memo(({ level, muted = false }) => {
  const cfg     = getLevelConfig(level);
  const trackBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box w="130px">
      <Flex justify="space-between" mb="3px">
        <Text fontSize="9px" fontWeight="800" color={muted ? 'gray.400' : `${cfg.color}.500`} letterSpacing="0.08em" textTransform="uppercase">
          {cfg.label}
        </Text>
        <Text fontSize="9px" color="gray.400" fontWeight="600">{cfg.value}%</Text>
      </Flex>
      <Box bg={trackBg} borderRadius="full" h="7px" overflow="hidden">
        <Box
          h="100%" bgGradient={muted ? 'linear(to-r, gray.300, gray.400)' : cfg.grad} borderRadius="full"
          w={`${cfg.value}%`}
          transition="width 0.6s ease-out"
        />
      </Box>
    </Box>
  );
});
WaterFillBar.displayName = 'WaterFillBar';
// --- END UPDATE

// ═════════════════════════════════════════════════════════════════════════════
// LIVE STATUS DOT
// --- UPDATE: V5 — replaced by LiveStatusIndicator (./sensors/SensorLiveUi), which uses the
// shared live-state model and plays a one-shot ping per reading instead of an infinite pulse.
// --- END UPDATE
// ═════════════════════════════════════════════════════════════════════════════

// ═════════════════════════════════════════════════════════════════════════════
// TREND ICON
// ═════════════════════════════════════════════════════════════════════════════

const TrendIcon = ({ current, previous }) => {
  const order = { EMPTY: 0, LOW: 1, MEDIUM: 2, HIGH: 3 };

  if (!current || !previous) return <Icon as={MdTrendingFlat} color="gray.300" boxSize={4} />;

  const c = order[current] ?? -1;
  const p = order[previous] ?? -1;

  if (p === -1 || c === p) return <Icon as={MdTrendingFlat}  color="gray.400"   boxSize={4} />;

  return c > p
    ? <Icon as={MdTrendingUp}   color="cyan.500"   boxSize={4} />
    : <Icon as={MdTrendingDown} color="orange.500" boxSize={4} />;
};

// ═════════════════════════════════════════════════════════════════════════════
// ALERT CHIP
// ═════════════════════════════════════════════════════════════════════════════

const AlertChip = ({ level, muted, isOnline }) => {
  if (!isOnline) return <Badge colorScheme="gray" borderRadius="full" px={2} fontSize="9px">OFFLINE</Badge>;
  if (muted)     return <Icon as={MdNotificationsOff} color="gray.400" boxSize={4} />;

  if (level === 'HIGH') return (
    <Badge colorScheme="cyan" borderRadius="full" px={2} fontSize="9px" fontWeight="800">
      <HStack spacing="3px"><Icon as={MdInfo}    boxSize={3}/><Text>FULL</Text></HStack>
    </Badge>
  );
  if (level === 'EMPTY') return (
    <Badge colorScheme="red" borderRadius="full" px={2} fontSize="9px" fontWeight="800" variant="solid">
      <HStack spacing="3px"><Icon as={MdError}   boxSize={3}/><Text>EMPTY</Text></HStack>
    </Badge>
  );
  if (level === 'LOW') return (
    <Badge colorScheme="orange" borderRadius="full" px={2} fontSize="9px" fontWeight="800">
      <HStack spacing="3px"><Icon as={MdWarning} boxSize={3}/><Text>LOW</Text></HStack>
    </Badge>
  );
  if (level === 'MEDIUM') return <Icon as={MdCheckCircle} color="green.400" boxSize={4} />;

  return <Text fontSize="xs" color="gray.400">-</Text>;
};

// ═════════════════════════════════════════════════════════════════════════════
// SUMMARY STATS BAR
// ═════════════════════════════════════════════════════════════════════════════

// --- UPDATE: optional activeLevel / onSelectLevel make the cards act as level filters
const SummaryBar = ({ sensors, readings, activeLevel = 'all', onSelectLevel }) => {
// --- END UPDATE
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.100', 'gray.700');
  // --- UPDATE
  const activeRing = useColorModeValue('0 0 0 2px var(--chakra-colors-cyan-300)', '0 0 0 2px var(--chakra-colors-cyan-600)');
  const noDataCount = useMemo(
    () => sensors.filter((s) => !readings[s.device_id]?.level).length,
    [sensors, readings]
  );
  // --- END UPDATE

  const iconBgCyan   = useColorModeValue('cyan.50',   'cyan.900');
  const iconBgBlue   = useColorModeValue('blue.50',   'blue.900');
  const iconBgOrange = useColorModeValue('orange.50', 'orange.900');
  const iconBgRed    = useColorModeValue('red.50',    'red.900');

  const counts = useMemo(() => {
    const c = { HIGH: 0, MEDIUM: 0, LOW: 0, EMPTY: 0 };
    sensors.forEach(s => {
      const l = readings[s.device_id]?.level;
      if (l) c[l] = (c[l] || 0) + 1;
    });
    return c;
  }, [sensors, readings]);

  const items = [
    { key: 'HIGH',   color: 'cyan',   iconBg: iconBgCyan,   icon: MdWaves,            label: 'High'   },
    { key: 'MEDIUM', color: 'blue',   iconBg: iconBgBlue,   icon: MdOutlineWaterDrop, label: 'Medium' },
    { key: 'LOW',    color: 'orange', iconBg: iconBgOrange, icon: MdWarning,          label: 'Low'    },
    { key: 'EMPTY',  color: 'red',    iconBg: iconBgRed,    icon: MdError,            label: 'Empty'  },
  ];

  return (
    // --- UPDATE: fragment so the "no data" note can sit under the grid
    <>
    {/* --- END UPDATE */}
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={noDataCount > 0 ? 2 : 5}>
      {items.map(({ key, color, iconBg, icon, label }) => (
        <MotionBox key={key} whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
          <Box
            bg={cardBg} borderWidth="1px" borderColor={border}
            borderRadius="xl" p={3}
            borderTopWidth="3px" borderTopColor={`${color}.400`}
            // --- UPDATE: click to filter by this level (click again to clear)
            as={onSelectLevel ? 'button' : 'div'}
            w="100%"
            textAlign="left"
            cursor={onSelectLevel ? 'pointer' : 'default'}
            boxShadow={activeLevel === key ? activeRing : undefined}
            onClick={onSelectLevel ? () => onSelectLevel(activeLevel === key ? 'all' : key) : undefined}
            aria-pressed={onSelectLevel ? activeLevel === key : undefined}
            aria-label={onSelectLevel ? `Filter by ${label} level (${counts[key]})` : undefined}
            // --- END UPDATE
          >
            <Flex align="center" justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="10px" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="0.07em">{label}</Text>
                <Text fontSize="2xl" fontWeight="800" color={`${color}.500`} lineHeight="1.1">{counts[key]}</Text>
              </VStack>
              <Box p={2} bg={iconBg} borderRadius="lg">
                <Icon as={icon} color={`${color}.500`} boxSize={5} />
              </Box>
            </Flex>
          </Box>
        </MotionBox>
      ))}
    </SimpleGrid>
    {/* --- UPDATE: sensors without any reading */}
    {noDataCount > 0 && (
      <Text fontSize="10px" color="gray.500" mb={4} px={1}>
        <Icon as={MdSensorsOff} boxSize={3} mr={1} verticalAlign="-2px" />
        {noDataCount} sensor{noDataCount > 1 ? 's have' : ' has'} not reported a level yet
      </Text>
    )}
    </>
    // --- END UPDATE
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// DATA QUALITY INDICATOR
// ═════════════════════════════════════════════════════════════════════════════

const DataQualityIndicator = ({ score }) => {
  const color = score >= 80 ? 'green' : score >= 60 ? 'yellow' : score >= 40 ? 'orange' : 'red';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Poor';

  return (
    <Tooltip label={`Data Quality: ${label} (${score}%)`} hasArrow>
      <Box w="40px">
        <Progress value={score} colorScheme={color} size="xs" borderRadius="full" />
      </Box>
    </Tooltip>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// DEBUG PANEL
// ═════════════════════════════════════════════════════════════════════════════

const DebugPanel = ({ debuggerInstance, isOpen, onClose }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.100', 'gray.700');
  const metrics = debuggerInstance.getMetrics();
  const logs    = debuggerInstance.getLogs();

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={border}>
          <HStack>
            <Icon as={MdBugReport} color="purple.500" />
            <Text fontWeight="700">Debug Panel</Text>
          </HStack>
        </DrawerHeader>
        <DrawerBody py={4}>
          <VStack spacing={4} align="stretch">
            {/* Metrics */}
            <Box>
              <Text fontWeight="700" mb={3} fontSize="sm">Performance Metrics</Text>
              <SimpleGrid columns={2} spacing={2}>
                {Object.entries(metrics).map(([key, value]) => (
                  <Box key={key} p={2} bg={cardBg} borderRadius="md" borderWidth="1px" borderColor={border}>
                    <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">{key}</Text>
                    <Text fontSize="lg" fontWeight="800">{value}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Logs */}
            <Box>
              <Text fontWeight="700" mb={3} fontSize="sm">Recent Logs ({logs.length})</Text>
              <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
                {logs.slice(-20).reverse().map((log, i) => (
                  <Box key={i} p={2} bg={cardBg} borderRadius="md" borderWidth="1px" borderColor={border} fontSize="11px">
                    <Flex justify="space-between" mb={1}>
                      <Badge colorScheme={log.level === 'error' ? 'red' : log.level === 'warn' ? 'orange' : 'blue'} fontSize="9px">
                        {log.level}
                      </Badge>
                      <Text color="gray.400" fontSize="9px">{new Date(log.timestamp).toLocaleTimeString()}</Text>
                    </Flex>
                    <Text fontWeight="600">{log.message}</Text>
                    {log.data && <Code fontSize="9px" mt={1}>{JSON.stringify(log.data)}</Code>}
                  </Box>
                ))}
              </VStack>
            </Box>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// HISTORY DRAWER
// ═════════════════════════════════════════════════════════════════════════════

const HistoryDrawer = ({ isOpen, onClose, deviceId, history = [] }) => {
  const border = useColorModeValue('gray.100', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  // --- UPDATE: analytics, level filter, export
  const statBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const [historyFilter, setHistoryFilter] = useState('all');
  const stats = useMemo(() => computeHistoryStats(history), [history]);
  const filteredHistory = useMemo(
    () => (historyFilter === 'all' ? history || [] : (history || []).filter((e) => e.level === historyFilter)),
    [history, historyFilter]
  );
  useEffect(() => {
    if (!isOpen) setHistoryFilter('all');
  }, [isOpen, deviceId]);
  const exportHistoryCSV = useCallback(() => {
    const rows = [['Device ID', 'Level', 'Fill %', 'Timestamp']].concat(
      (history || []).map((e) => [deviceId, getLevelConfig(e.level).label, getLevelConfig(e.level).value, e.timestamp ? new Date(e.timestamp).toISOString() : ''])
    );
    downloadBlob(rows.map((r) => r.map(csvEscape).join(',')).join('\n'), 'text/csv', `level-history-${deviceId}-${Date.now()}.csv`);
  }, [history, deviceId]);
  // --- END UPDATE

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent borderLeftRadius="2xl">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={border}>
          <HStack spacing={3}>
            <Box p={2} bgGradient="linear(135deg, cyan.400, blue.500)" borderRadius="lg">
              <Icon as={MdHistory} color="white" boxSize={5} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontWeight="700" fontSize="md">Level History</Text>
              <Text fontSize="11px" color="gray.500" fontFamily="mono">{deviceId}</Text>
            </VStack>
          </HStack>
        </DrawerHeader>
        <DrawerBody py={4}>
          {!history || history.length === 0 ? (
            <VStack py={14} spacing={3} color="gray.400">
              <Icon as={MdHistory} boxSize={12} opacity={0.3} />
              <Text fontSize="sm" fontWeight="600">No history recorded yet</Text>
              <Text fontSize="xs" textAlign="center" maxW="260px">
                History entries appear as readings arrive via WebSocket.
              </Text>
            </VStack>
          ) : (
            <VStack spacing={3} align="stretch">
              {/* --- UPDATE: history analytics header */}
              <Box p={3} borderRadius="xl" borderWidth="1px" borderColor={border} bg={statBg}>
                <Flex justify="space-between" align="center" mb={2}>
                  <HStack spacing={1.5}>
                    <Icon as={MdTimeline} color="cyan.500" />
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">
                      Level Trajectory
                    </Text>
                  </HStack>
                  <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<MdFileDownload />} onClick={exportHistoryCSV}>
                    CSV
                  </Button>
                </Flex>
                <LevelStepChart chronological={stats.chronological} />
                <SimpleGrid columns={3} spacing={2} mt={3}>
                  <Box>
                    <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase">Readings</Text>
                    <Text fontSize="md" fontWeight="800">{stats.chronological.length}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase">Transitions</Text>
                    <Text fontSize="md" fontWeight="800">{stats.transitions}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase">Span</Text>
                    <Text fontSize="md" fontWeight="800">{formatDuration(stats.spanMs)}</Text>
                  </Box>
                </SimpleGrid>
                {stats.totalTimed > 0 && (
                  <Box mt={3}>
                    <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase" mb={1}>
                      Time in level
                    </Text>
                    <Flex h="8px" borderRadius="full" overflow="hidden">
                      {['HIGH', 'MEDIUM', 'LOW', 'EMPTY'].map((lvl) =>
                        stats.timeIn[lvl] > 0 ? (
                          <Tooltip key={lvl} hasArrow label={`${getLevelConfig(lvl).label}: ${formatDuration(stats.timeIn[lvl])}`}>
                            <Box w={`${(stats.timeIn[lvl] / stats.totalTimed) * 100}%`} bg={`${getLevelConfig(lvl).color}.400`} />
                          </Tooltip>
                        ) : null
                      )}
                    </Flex>
                  </Box>
                )}
                <Wrap spacing={1.5} mt={3}>
                  {['all', 'HIGH', 'MEDIUM', 'LOW', 'EMPTY'].map((lvl) => (
                    <WrapItem key={lvl}>
                      <Button
                        size="xs"
                        borderRadius="full"
                        variant={historyFilter === lvl ? 'solid' : 'outline'}
                        colorScheme={lvl === 'all' ? 'gray' : getLevelConfig(lvl).color}
                        onClick={() => setHistoryFilter(lvl)}
                      >
                        {lvl === 'all' ? `All (${(history || []).length})` : `${getLevelConfig(lvl).label} (${stats.counts[lvl]})`}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
              {/* --- END UPDATE */}
              {/* --- UPDATE: filtered list; stagger capped */}
              {filteredHistory.map((entry, idx) => {
                // --- END UPDATE
                const cfg = getLevelConfig(entry.level);
                return (
                  <MotionBox
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    // --- UPDATE
                    transition={{ delay: Math.min(idx * 0.035, 0.5) }}
                    // --- END UPDATE
                  >
                    <Box
                      p={4} bg={cardBg} borderRadius="xl"
                      borderWidth="1px" borderColor={border}
                      borderLeftWidth="4px" borderLeftColor={`${cfg.color}.400`}
                    >
                      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                        <HStack spacing={3}>
                          <Badge colorScheme={cfg.color} borderRadius="full" px={2} fontSize="10px" fontWeight="700">
                            {cfg.label}
                          </Badge>
                          <WaterFillBar level={entry.level} />
                        </HStack>
                        <Text fontSize="11px" color="gray.400" fontFamily="mono">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}
                        </Text>
                      </Flex>
                    </Box>
                  </MotionBox>
                );
              })}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// --- UPDATE: V5 MEMOISED ROW
// The row markup used to be rendered inline by the table, so every reading that
// reached the page re-rendered every row (and its menus). It is now a memo
// component whose props are primitives or cached objects; handlers are stable.
// ═════════════════════════════════════════════════════════════════════════════

const WaterLevelRow = React.memo(function WaterLevelRow({
  sensor, reading, history, linkedPipelineId, isMuted, isChecked, isSelected, isExpanded,
  visibleColumns, columnCount, ageLabel, ai, thresholds, colors, canSelect,
  onSelect, onToggleSelect, onToggleExpanded, onOpenHistory, onToggleMute, onCopy,
  onLink, onLinkAsset, onUnlink,
}) {
  const r   = reading || {};
  const cfg = getLevelConfig(r.level);

  // Polymorphic link resolution
  const isAssetLink = sensor.linked_entity_type === 'asset' && sensor.linked_entity_id;
  const isPipeLink  = !!linkedPipelineId || sensor.linked_entity_type === 'pipeline';
  const pipeLabel   = sensor.linked_entity_id || linkedPipelineId;

  const prevLevel = history && history.length > 1 ? history[1].level : null;
  const measuredAt = sensor.__row?.measuredMs ? new Date(sensor.__row.measuredMs) : null;
  const heardAt    = sensor.__row?.lastHeardMs ? new Date(sensor.__row.lastHeardMs) : null;
  const liveMeta   = LIVE_STATE_META[sensor.liveState] || LIVE_STATE_META.no_data;

  return [
    <Tr
      key={sensor.__key}
      cursor="pointer"
      borderBottomWidth="1px"
      borderBottomColor={colors.border}
      bg={isSelected ? colors.selectedRowBg : 'transparent'}
      borderLeftWidth={isSelected ? '4px' : '0px'}
      borderLeftColor="cyan.400"
      onClick={() => onSelect(sensor)}
      _hover={{ bg: isSelected ? colors.selectedRowBg : colors.rowHoverBg }}
      opacity={sensor.isOnline ? 1 : 0.6}
      transition="background-color 0.15s ease"
      data-device-id={sensor.device_id}
      data-live-state={sensor.liveState}
      aria-selected={isSelected || undefined}
    >
      {/* Row checkbox */}
      <Td py={3} px={4} onClick={(e) => e.stopPropagation()}>
        <Checkbox
          isChecked={isChecked}
          onChange={() => onToggleSelect(sensor.device_id)}
          colorScheme="cyan"
          aria-label={`Select ${sensor.device_id}`}
        />
      </Td>

      {/* Live status (transmitting / online / delayed / offline / no data) */}
      {visibleColumns.status && (
        <Td py={3} px={4}>
          <LiveStatusIndicator row={sensor.__row} thresholds={thresholds} ageLabel={ageLabel} />
        </Td>
      )}

      {/* Sensor ID + name */}
      {visibleColumns.id && (
        <Td py={3} px={4}>
          <VStack align="start" spacing={0}>
            <HStack spacing={1} role="group">
              <Text fontWeight="700" fontSize="xs" fontFamily="mono"
                color={sensor.isOnline ? `${cfg.color}.600` : 'gray.500'}
                letterSpacing="0.04em">
                {sensor.device_id}
              </Text>
              <IconButton
                icon={<MdContentCopy />}
                size="xs" h="16px" minW="16px" variant="ghost"
                opacity={0} _groupHover={{ opacity: 1 }} _focusVisible={{ opacity: 1 }}
                onClick={(e) => { e.stopPropagation(); onCopy(sensor.device_id); }}
                aria-label={`Copy ${sensor.device_id}`}
              />
            </HStack>
            {sensor.name && (
              <Text fontSize="10px" color="gray.400">{sensor.name}</Text>
            )}
            {!sensor.__registered && (
              <Tooltip hasArrow label="This device is sending water-level readings but is not in the water-level sensor register (not registered yet, or registered with another type). Register or retype it to link it.">
                <Badge colorScheme="yellow" variant="subtle" fontSize="8px" mt={0.5}>NOT IN REGISTER</Badge>
              </Tooltip>
            )}
            {sensor.__retained && (
              <Tooltip hasArrow label="Missing from the last sensor list refresh — kept on screen while the list reloads.">
                <Badge colorScheme="gray" variant="subtle" fontSize="8px" mt={0.5}>SYNCING</Badge>
              </Tooltip>
            )}
          </VStack>
        </Td>
      )}

      {/* Level badge — last known level stays visible (greyed) when the sensor is offline */}
      {visibleColumns.level && (
        <Td py={3} px={4}>
          {r.level ? (
            <Tooltip hasArrow isDisabled={sensor.isOnline} label={`Last known level · ${liveMeta.label.toLowerCase()}`}>
              <Badge colorScheme={sensor.isOnline ? cfg.color : 'gray'} px={3} py={1} borderRadius="full" fontSize="10px" fontWeight="800" letterSpacing="0.05em">
                <HStack spacing="3px">
                  <Icon as={cfg.icon} boxSize={3} />
                  <Text>{cfg.label}</Text>
                </HStack>
              </Badge>
            </Tooltip>
          ) : (
            <Flex align="center">
              <Icon as={MdSensorsOff} mr={2} color="gray.400" />
              <Text fontSize="10px" color="gray.400">No data</Text>
            </Flex>
          )}
        </Td>
      )}

      {/* Fill bar */}
      {visibleColumns.fill && (
        <Td py={3} px={4}><WaterFillBar level={r.level} muted={!sensor.isOnline} /></Td>
      )}

      {/* Trend icon */}
      {visibleColumns.trend && (
        <Td py={3} px={4}><TrendIcon current={r.level} previous={prevLevel} /></Td>
      )}

      {/* Alert chip + FlowSense AI */}
      {visibleColumns.alert && (
        <Td py={3} px={4} onClick={(e) => e.stopPropagation()}>
          <VStack align="start" spacing={1}>
            <AlertChip level={r.level} muted={isMuted} isOnline={sensor.isOnline} />
            {ai && <SensorAiBadge ai={ai} kind="water_level" isOnline={sensor.isOnline} />}
          </VStack>
        </Td>
      )}

      {/* Polymorphic Linked Entity cell */}
      {visibleColumns.pipeline && (
        <Td py={3} px={4}>
          {isAssetLink ? (
            <Tooltip label={`Bound to Asset ID: ${sensor.linked_entity_id}`} hasArrow>
              <Tag size="sm" colorScheme="teal" borderRadius="full" variant="subtle">
                <TagLeftIcon as={MdOutlineWaterDrop} boxSize={3} />
                <TagLabel fontWeight="700" fontSize="10px">Asset #{sensor.linked_entity_id}</TagLabel>
              </Tag>
            </Tooltip>
          ) : isPipeLink ? (
            <Tooltip label={`Bound to Pipeline ID: ${pipeLabel}`} hasArrow>
              <Tag size="sm" colorScheme="purple" borderRadius="full" variant="subtle">
                <TagLeftIcon as={MdLink} boxSize={3} />
                <TagLabel fontWeight="700" fontSize="10px">Pipe #{pipeLabel}</TagLabel>
              </Tag>
            </Tooltip>
          ) : (
            <Text fontSize="11px" color="gray.400" fontStyle="italic">Unassigned</Text>
          )}
        </Td>
      )}

      {/* Last seen: age of the last data received; measured time on hover */}
      {visibleColumns.lastSeen && (
        <Td py={3} px={4}>
          <Tooltip hasArrow openDelay={300}
            label={!measuredAt && !heardAt ? 'No reading received yet'
              : `Measured ${measuredAt ? measuredAt.toLocaleString() : '—'} · last data received ${heardAt ? heardAt.toLocaleString() : '—'}`}
          >
            <Box>
              <Text fontSize="11px" color="gray.400" fontFamily="mono">
                {measuredAt ? measuredAt.toLocaleTimeString() : '—'}
              </Text>
              {ageLabel && (
                <Text fontSize="9px" fontWeight="700" color={`${liveMeta.color}.500`}>
                  {ageLabel}
                </Text>
              )}
            </Box>
          </Tooltip>
        </Td>
      )}

      {/* Actions */}
      {visibleColumns.actions && (
        <Td py={3} px={4} onClick={(e) => e.stopPropagation()}>
          <HStack spacing={1}>
            <Tooltip label={isExpanded ? 'Hide details' : 'Show details'} hasArrow>
              <IconButton
                icon={isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                size="xs" variant={isExpanded ? 'solid' : 'ghost'} colorScheme="cyan" borderRadius="lg"
                onClick={() => onToggleExpanded(sensor.device_id)}
                aria-label={isExpanded ? 'Hide details' : 'Show details'}
                aria-expanded={isExpanded}
              />
            </Tooltip>
            <Tooltip label="View History" hasArrow>
              <IconButton
                icon={<MdHistory />}
                size="xs" variant="ghost" colorScheme="gray" borderRadius="lg"
                onClick={() => onOpenHistory(sensor.device_id)}
                aria-label="History"
              />
            </Tooltip>
            <Tooltip label={isMuted ? 'Unmute alerts' : 'Mute alerts'} hasArrow>
              <IconButton
                icon={isMuted ? <MdNotificationsOff /> : <MdNotifications />}
                size="xs" variant="ghost"
                colorScheme={isMuted ? 'gray' : 'orange'} borderRadius="lg"
                onClick={() => onToggleMute(sensor.device_id)}
                aria-label="Toggle mute"
              />
            </Tooltip>
            {/* Lazy: the menu list mounts only when opened */}
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                icon={<Icon as={MdMoreVert} />}
                size="xs" variant="ghost" borderRadius="lg"
                aria-label="More actions"
              />
              <MenuList zIndex={10}>
                {canSelect && (
                  <MenuItem icon={<Icon as={MdVisibility} />} onClick={() => onSelect(sensor)}>
                    View Details
                  </MenuItem>
                )}
                {!isAssetLink && !isPipeLink ? (
                  <>
                    <MenuItem icon={<Icon as={MdLink} />} color="purple.500" fontWeight="500" onClick={() => onLink(sensor)}>
                      Link to Pipeline
                    </MenuItem>
                    <MenuItem icon={<Icon as={MdOutlineWaterDrop} />} color="teal.500" fontWeight="500" onClick={() => onLinkAsset(sensor)}>
                      Bind to Asset (Well/Tank)
                    </MenuItem>
                  </>
                ) : (
                  <MenuItem icon={<Icon as={MdLinkOff} />} color="red.500" fontWeight="500" onClick={() => onUnlink(sensor.device_id)}>
                    Unlink Entity
                  </MenuItem>
                )}
                <MenuItem icon={<Icon as={MdHistory} />} onClick={() => onOpenHistory(sensor.device_id)}>
                  View History
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Td>
      )}
    </Tr>,
    isExpanded && (() => {
      const readingCheck = r.level ? DataQualityValidator.validateReading(r) : { issues: ['No reading received'], score: 0 };
      const histStats = computeHistoryStats(history || []);
      return (
        <Tr key={`${sensor.__key}-details`} bg={colors.detailRowBg}>
          <Td colSpan={columnCount} px={6} py={4} onClick={(e) => e.stopPropagation()}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
              <Box>
                <Text fontSize="9px" fontWeight="800" color={colors.thColor} textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                  Recent levels
                </Text>
                <LevelHistoryStrip history={history} />
                <HStack spacing={4} mt={2} fontSize="10px" color="gray.500">
                  <Text><b>{histStats.transitions}</b> transitions</Text>
                  <Text>span <b>{formatDuration(histStats.spanMs)}</b></Text>
                </HStack>
                <HStack spacing={2} mt={3}>
                  <LiveStatusIndicator row={sensor.__row} thresholds={thresholds} ageLabel={ageLabel} />
                  <Text fontSize="10px" color="gray.500">{ageLabel ? `last data ${ageLabel}` : 'no data yet'}</Text>
                </HStack>
              </Box>
              <Box>
                <Text fontSize="9px" fontWeight="800" color={colors.thColor} textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                  Data quality · {readingCheck.score}%
                </Text>
                {readingCheck.issues.length === 0 ? (
                  <HStack spacing={1} color="green.500" fontSize="xs">
                    <Icon as={MdCheckCircle} /><Text fontWeight="600">Reading valid and fresh</Text>
                  </HStack>
                ) : (
                  <VStack align="start" spacing={0.5}>
                    {readingCheck.issues.map((issue) => (
                      <HStack key={issue} spacing={1} color="orange.500" fontSize="xs">
                        <Icon as={MdWarning} boxSize={3} /><Text>{issue}</Text>
                      </HStack>
                    ))}
                  </VStack>
                )}
                {measuredAt && (
                  <Text fontSize="10px" color="gray.500" mt={2} fontFamily="mono">
                    {measuredAt.toLocaleString()}
                  </Text>
                )}
              </Box>
              <Box>
                {ai ? <SensorAiPanel ai={ai} kind="water_level" /> : null}
                <HStack spacing={2} mt={3}>
                  <Button size="xs" variant="outline" leftIcon={<MdHistory />} onClick={() => onOpenHistory(sensor.device_id)}>Full history</Button>
                  {canSelect && (
                    <Button size="xs" variant="outline" colorScheme="cyan" leftIcon={<MdVisibility />} onClick={() => onSelect(sensor)}>Details</Button>
                  )}
                </HStack>
              </Box>
            </SimpleGrid>
          </Td>
        </Tr>
      );
    })(),
  ];
});
// --- END UPDATE

// --- UPDATE: V5 — hoisted out of render (it was re-created on every render)
const SortChevron = ({ col, sortKey, sortDir }) => {
  if (sortKey !== col) return <Icon as={MdSort} boxSize={3} color="gray.400" ml={1} />;
  return <Icon as={sortDir === 'asc' ? MdTrendingUp : MdTrendingDown} boxSize={3} color="cyan.400" ml={1} />;
};

/** Stable function identity that always calls the latest prop (keeps memoised rows memoised). */
const useStableCallback = (fn) => {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args) => (ref.current ? ref.current(...args) : undefined), []);
};

/** Re-key a { [deviceId]: value } map by the registry's display ids (case-insensitive). */
const rekeyByRows = (rows, source) => {
  const out = {};
  if (!source) return out;
  const byNorm = new Map();
  Object.keys(source).forEach((k) => byNorm.set(normalizeId(k), source[k]));
  rows.forEach((row) => {
    const v = source[row.id] ?? byNorm.get(row.key);
    if (v !== undefined) out[row.id] = v;
  });
  return out;
};

/** Keeps the previous object when every value is identical (shallow). */
const useShallowStable = (obj) => {
  const ref = useRef(obj);
  const prev = ref.current;
  if (prev !== obj) {
    const a = Object.keys(prev);
    const b = Object.keys(obj);
    if (a.length !== b.length || b.some((k) => prev[k] !== obj[k])) ref.current = obj;
  }
  return ref.current;
};
// --- END UPDATE

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

const WaterLevelTable = ({
  sensors          = [],
  readings         = {},
  links            = {},
  sensorHistory    = {},
  selectedSensorId = null, // 🚀 Highlighting prop (bi-directional)
  aiPredictions    = {},
  isLoading        = false,
  onLink,
  onLinkAsset,     // Asset/Well/Tank binding handler
  onUnlink,
  onHistory,
  onSensorSelect,  // Sensor detail view handler (also fired on row click)
  // --- UPDATE: optional V4 props
  pageSize         = DEFAULT_PAGE_SIZE,
  persistKey       = 'flowsense.waterLevelTable.v4', // pass null to disable persistence
  maxTableHeight   = null,                           // e.g. '65vh' → scrollable body with sticky header
  // --- END UPDATE
  // --- UPDATE: V5 optional props
  liveThresholds      = null,  // overrides for DEFAULT_LIVE_THRESHOLDS (ms)
  includeUnregistered = true,  // show devices sending readings that are not in `sensors`
  // --- END UPDATE
}) => {
  // --- UPDATE: persisted preferences (read once)
  const initialPrefs = useRef(null);
  if (initialPrefs.current === null) initialPrefs.current = loadPrefs(persistKey);
  // --- END UPDATE
  // ═══ Theme ═══
  const border        = useColorModeValue('gray.100', 'gray.700');
  const headerBg      = useColorModeValue('gray.50',  'gray.750');
  const thColor       = useColorModeValue('gray.500', 'gray.400');
  const inputBg       = useColorModeValue('white',    'gray.700');
  const emptyColor    = useColorModeValue('gray.400', 'gray.500');
  const emptyCodeBg   = useColorModeValue('gray.100', 'gray.700');
  const selectedRowBg = useColorModeValue('cyan.50',  'rgba(0, 181, 216, 0.18)');
  // Hoisted from the row map (hooks cannot be called inside a loop)
  const rowHoverBg    = useColorModeValue('gray.50',  'whiteAlpha.50');
  // --- UPDATE
  const detailRowBg   = useColorModeValue('gray.50',  'whiteAlpha.50');
  const chipActiveBg  = useColorModeValue('cyan.50',  'rgba(0, 181, 216, 0.15)');
  const stickyHeadBg  = useColorModeValue('gray.50',  'gray.800');
  // --- END UPDATE

  // ═══ Core State ═══
  const [search,      setSearch]      = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  // --- UPDATE: sort + muted restored from persisted preferences
  const [sortKey,     setSortKey]     = useState(() => initialPrefs.current.sortKey || 'device_id');
  const [sortDir,     setSortDir]     = useState(() => initialPrefs.current.sortDir || 'asc');
  const [muted,       setMuted]       = useState(() => initialPrefs.current.muted || {});
  // --- END UPDATE
  const [activeSensor, setActiveSensor] = useState(null);

  // --- UPDATE: V4 state
  const [quickFilter,   setQuickFilter]   = useState('all'); // all | alerts | ai | unassigned | muted
  const [density,       setDensity]       = useState(() => initialPrefs.current.density || 'comfortable');
  const [visibleCount,  setVisibleCount]  = useState(pageSize);
  const [expanded,      setExpanded]      = useState(() => new Set());
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  // --- UPDATE: V5 — shared live clock
  const clockTick = useNow(LIVE_CLOCK_MS);
  // --- END UPDATE
  const searchInputRef = useRef(null);
  const cancelUnlinkRef = useRef(null);
  const deferredSearch =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    typeof React.useDeferredValue === 'function' ? React.useDeferredValue(search) : search;
  // --- END UPDATE

  // =========================================================================
  // GLOBAL CONNECTION STATE
  // =========================================================================
  const { isConnected } = useNotifications();

  // ═══ Enhanced State ═══
  const [filterTab,       setFilterTab]       = useState(0); // 0=All, 1=Online, 2=Offline
  const [selected,        setSelected]        = useState(new Set());
  const [autoRefresh,     setAutoRefresh]     = useState(false);
  // --- UPDATE: V5 — 15 s, visible rows only (was every sensor every 5 s → N requests / 5 s)
  const refreshInterval = AUTO_REFRESH_MS;
  const visibleIdsRef = useRef([]);
  // --- END UPDATE
  // --- UPDATE: setter exposed (column picker) + restored from persisted preferences
  const [visibleColumns, setVisibleColumns] = useState(() => ({
    status: true, id: true, level: true, fill: true, trend: true,
    alert: true, pipeline: true, lastSeen: true, actions: true,
    ...(initialPrefs.current.columns || {}),
  }));
  // --- END UPDATE

  // ═══ Refs ═══
  const debuggerRef       = useRef(new WaterLevelDebugger()).current;
  const renderCount       = useRef(0);
  const lastDataUpdateRef = useRef(Date.now());

  // ═══ Hooks ═══
  const { isOpen: historyOpen, onOpen: historyOnOpen, onClose: historyOnClose } = useDisclosure();
  const { isOpen: debugOpen,   onOpen: debugOnOpen,   onClose: debugOnClose   } = useDisclosure();
  const toast = useToast();

  // ═══ Track render count ═══
  useEffect(() => {
    renderCount.current++;
    debuggerRef.incrementMetric('renders');
    debuggerRef.log('debug', `Component rendered (${renderCount.current})`);
  });

  // ═══ Track data updates ═══
  useEffect(() => {
    const now = Date.now();
    const timeSinceUpdate = now - lastDataUpdateRef.current;
    if (timeSinceUpdate > 100) {
      debuggerRef.incrementMetric('dataUpdates');
      debuggerRef.log('info', 'Data updated', { sensors: sensors.length, readings: Object.keys(readings || {}).length });
      lastDataUpdateRef.current = now;
    }
  }, [sensors, readings, debuggerRef]);

  // ═══ Auto-refresh ═══
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      // --- UPDATE: V5
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      debuggerRef.log('debug', 'Auto-refresh triggered', { rows: visibleIdsRef.current.length });
      if (onHistory) visibleIdsRef.current.forEach((deviceId) => onHistory(deviceId));
      // --- END UPDATE
    }, refreshInterval);
    return () => clearInterval(id);
  }, [autoRefresh, refreshInterval, onHistory, debuggerRef]);

  // --- UPDATE: V5 — the 30 s clock interval is replaced by useNow(LIVE_CLOCK_MS) above
  // Persist preferences
  useEffect(() => {
    savePrefs(persistKey, { columns: visibleColumns, density, sortKey, sortDir, muted });
  }, [persistKey, visibleColumns, density, sortKey, sortDir, muted]);

  // Reset paging when the result set changes shape
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [deferredSearch, levelFilter, filterTab, quickFilter, sortKey, sortDir, pageSize]);

  // Keyboard shortcuts: "/" focuses search, Esc clears selection + expanded rows
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'Escape' && !typing) {
        setSelected((prev) => (prev.size ? new Set() : prev));
        setExpanded((prev) => (prev.size ? new Set() : prev));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  // --- END UPDATE

  // --- UPDATE: V5 — sticky live registry (replaces the WebSocket-gated 5-minute TTL).
  // Every lookup below goes through liveReadings / historyView / linksView, which are keyed by
  // the row's display id, so "uno-r4-01" readings match the "UNO-R4-01" sensor.
  const registry = useLiveSensorRegistry({ sensors, readings, now: clockTick, thresholds: liveThresholds, includeUnregistered });

  const rowViewCache = useRef(new WeakMap());
  const mergedSensors = useMemo(() => {
    const cache = rowViewCache.current;
    return registry.rows.map((row) => {
      const hit = cache.get(row);
      if (hit) return hit;
      const view = {
        ...(row.sensor || {}),
        device_id: row.id,
        isOnline: row.isOnline,
        liveState: row.liveState,
        __key: row.key,
        __row: row,
        __registered: row.registered,
        __retained: row.retained,
      };
      if (!row.registered && !view.type) view.type = 'water_level';
      cache.set(row, view);
      return view;
    });
  }, [registry.rows]);

  const liveReadings = useShallowStable(useMemo(() => {
    const out = {};
    registry.rows.forEach((row) => { if (row.reading) out[row.id] = row.reading; });
    return out;
  }, [registry.rows]));
  const historyView = useShallowStable(useMemo(() => rekeyByRows(registry.rows, sensorHistory), [registry.rows, sensorHistory]));
  const linksView   = useShallowStable(useMemo(() => rekeyByRows(registry.rows, links), [registry.rows, links]));

  // FlowSense AI per sensor — recomputed only when that sensor's inputs change
  const aiCacheRef = useRef(new Map());
  const aiByKey = useMemo(() => {
    const cache = aiCacheRef.current;
    const out = new Map();
    mergedSensors.forEach((s) => {
      const prediction = getSensorAi(s, aiPredictions);
      const levelHistory = historyView[s.device_id] || null;
      const prev = cache.get(s.__key);
      if (prev && prev.view === s && prev.history === levelHistory && prev.prediction === prediction) {
        out.set(s.__key, prev.ai);
        return;
      }
      const ai = deriveSensorAi({ kind: 'water_level', sensor: s, prediction, levelHistory: levelHistory || [] });
      cache.set(s.__key, { view: s, history: levelHistory, prediction, ai });
      out.set(s.__key, ai);
    });
    cache.forEach((_, k) => { if (!out.has(k)) cache.delete(k); });
    return out;
  }, [mergedSensors, historyView, aiPredictions]);
  const aiSummary = useMemo(() => summarizeAi([...aiByKey.values()]), [aiByKey]);
  const lastArrivalMs = useMemo(
    () => registry.rows.reduce((m, r) => (r.lastArrivalMs && r.lastArrivalMs > m ? r.lastArrivalMs : m), 0) || null,
    [registry.rows]
  );

  // ═══ Data quality assessment ═══ (moved below the registry so it scores what is displayed)
  const dataQuality = useMemo(() => {
    return DataQualityValidator.assessOverallQuality(mergedSensors, liveReadings);
  }, [mergedSensors, liveReadings]);

  const onlineCount  = registry.counts.online;
  const offlineCount = registry.counts.offline;
  // --- END UPDATE

  // --- UPDATE: quick-filter predicates + live counts
  const quickFilterPredicates = useMemo(() => ({
    all:        () => true,
    // --- UPDATE: V5 — registry-keyed lookups; AI flagged = flood ≥ threshold, critical health or an edge anomaly
    alerts:     (s) => s.isOnline && !muted[s.device_id] && ALERT_LEVELS.has(liveReadings[s.device_id]?.level),
    ai:         (s) => !!aiByKey.get(s.__key)?.flagged,
    unassigned: (s) => getLinkInfo(s, linksView).isUnassigned,
    muted:      (s) => !!muted[s.device_id],
  }), [muted, liveReadings, aiByKey, linksView]);
  // --- END UPDATE

  const quickFilterCounts = useMemo(() => {
    const counts = { all: mergedSensors.length, alerts: 0, ai: 0, unassigned: 0, muted: 0 };
    mergedSensors.forEach((s) => {
      ['alerts', 'ai', 'unassigned', 'muted'].forEach((k) => {
        if (quickFilterPredicates[k](s)) counts[k] += 1;
      });
    });
    return counts;
  }, [mergedSensors, quickFilterPredicates]);
  // --- END UPDATE

  // ═══ Filtered + sorted list ═══
  const displayed = useMemo(() => {
    debuggerRef.startMark('filterAndSort');
    let list = [...mergedSensors];

    // Filter by Online/Offline tab
    if (filterTab === 1) list = list.filter(s =>  s.isOnline);
    if (filterTab === 2) list = list.filter(s => !s.isOnline);

    // --- UPDATE: quick filter chip
    if (quickFilter !== 'all' && quickFilterPredicates[quickFilter]) {
      list = list.filter(quickFilterPredicates[quickFilter]);
    }
    // --- END UPDATE

    // Filter by search
    // --- UPDATE: deferred value + linked entity IDs are searchable
    if (deferredSearch) {
      debuggerRef.incrementMetric('filterOperations');
      const q = deferredSearch.toLowerCase();
      list = list.filter(s =>
        (s.device_id || '').toLowerCase().includes(q) ||
        (s.name      || '').toLowerCase().includes(q) ||
        String(s.linked_entity_id ?? linksView[s.device_id] ?? '').toLowerCase().includes(q)
      );
    }
    // --- END UPDATE

    // Filter by level
    if (levelFilter !== 'all') {
      debuggerRef.incrementMetric('filterOperations');
      list = list.filter(s => (liveReadings[s.device_id]?.level || 'UNKNOWN') === levelFilter);
    }

    // Sort
    debuggerRef.incrementMetric('sortOperations');
    const lvOrder = { HIGH: 3, MEDIUM: 2, LOW: 1, EMPTY: 0 };

    list.sort((a, b) => {
      let va, vb;

      if (sortKey === 'device_id') {
        va = a.device_id || '';
        vb = b.device_id || '';
      } else if (sortKey === 'level') {
        va = lvOrder[liveReadings[a.device_id]?.level] ?? -1;
        vb = lvOrder[liveReadings[b.device_id]?.level] ?? -1;
      } else if (sortKey === 'updated') {
        // --- UPDATE: V5 — when data was last heard (not the device timestamp)
        va = a.__row?.lastHeardMs || 0;
        vb = b.__row?.lastHeardMs || 0;
        // --- END UPDATE
      }
      // --- UPDATE: AI flood-risk sort
      // --- UPDATE: V5 — FlowSense AI score (flood probability, else severity)
      else if (sortKey === 'ai') {
        va = aiByKey.get(a.__key)?.score ?? -1;
        vb = aiByKey.get(b.__key)?.score ?? -1;
      }
      // --- END UPDATE
      // --- END UPDATE

      // --- UPDATE: V5 — stable tiebreak so equal rows never swap places between renders
      if (va === vb) return String(a.device_id).localeCompare(String(b.device_id), undefined, { numeric: true });
      // --- END UPDATE
      if (sortDir === 'asc') return va > vb ? 1 : -1;
      return va < vb ? 1 : -1;
    });

    debuggerRef.endMark('filterAndSort');
    return list;
    // --- UPDATE
  }, [mergedSensors, filterTab, liveReadings, deferredSearch, levelFilter, sortKey, sortDir, debuggerRef,
      quickFilter, quickFilterPredicates, linksView, aiByKey]);

  const pagedDisplayed = useMemo(() => displayed.slice(0, visibleCount), [displayed, visibleCount]);
  const remainingCount = displayed.length - pagedDisplayed.length;
  const columnCount    = 1 + Object.values(visibleColumns).filter(Boolean).length;
  // --- END UPDATE
  // --- UPDATE: V5 — auto-refresh only asks for the rows on screen
  visibleIdsRef.current = pagedDisplayed.map((s) => s.device_id);
  // --- END UPDATE

  // ═══ Handlers ═══
  const toggleSort = (col) => {
    if (sortKey === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(col); setSortDir('asc'); }
  };

  // --- UPDATE: V5 — stable handlers (memoised rows); toast moved out of the state updater
  // (React may call updaters twice in StrictMode, which showed the toast twice)
  const openHistory = useStableCallback((deviceId) => {
    setActiveSensor(deviceId);
    if (onHistory) onHistory(deviceId);
    historyOnOpen();
  });

  const toggleMute = useStableCallback((deviceId) => {
    const nextValue = !muted[deviceId];
    setMuted((prev) => ({ ...prev, [deviceId]: !prev[deviceId] }));
    toast({
      title: nextValue ? 'Alerts muted' : 'Alerts enabled',
      description: deviceId,
      status: 'info',
      duration: 2000,
      position: 'bottom-right',
    });
  });

  const selectRow   = useStableCallback((sensor) => onSensorSelect?.(sensor));
  const linkRow     = useStableCallback((sensor) => onLink?.(sensor));
  const linkAssetRow = useStableCallback((sensor) => onLinkAsset?.(sensor));
  const unlinkRow   = useStableCallback((deviceId) => onUnlink?.(deviceId));
  const selectedKey = selectedSensorId !== null && selectedSensorId !== undefined ? normalizeId(selectedSensorId) : null;
  const rowColors = useMemo(
    () => ({ selectedRowBg, rowHoverBg, detailRowBg, thColor, border }),
    [selectedRowBg, rowHoverBg, detailRowBg, thColor, border]
  );
  // --- END UPDATE

  // ═══ Batch operations ═══
  const toggleSelectAll = () => {
    if (selected.size === displayed.length) {
      setSelected(new Set());
      debuggerRef.log('info', 'Deselected all sensors');
    } else {
      setSelected(new Set(displayed.map(s => s.device_id)));
      debuggerRef.log('info', 'Selected all sensors', { count: displayed.length });
    }
  };

  // --- UPDATE: V5 — stable
  const toggleSelect = useCallback((deviceId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  }, []);
  // --- END UPDATE

  const bulkUnlink = () => {
    debuggerRef.log('info', 'Bulk unlink', { count: selected.size });
    const count = selected.size;
    selected.forEach(id => {
      if (onUnlink) onUnlink(id);
    });
    setSelected(new Set());
    toast({ title: 'Bulk Unlink', description: `Unlinked ${count} sensors`, status: 'success', duration: 3000 });
  };

  // --- UPDATE: V4 handlers
  const bulkSetMuted = (value) => {
    const ids = Array.from(selected);
    setMuted((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = value; });
      return next;
    });
    debuggerRef.log('info', value ? 'Bulk mute' : 'Bulk unmute', { count: ids.length });
    toast({
      title: value ? 'Alerts muted' : 'Alerts enabled',
      description: `${ids.length} sensor${ids.length > 1 ? 's' : ''}`,
      status: 'info', duration: 2000, position: 'bottom-right',
    });
  };

  const bulkRefreshHistory = () => {
    if (!onHistory) return;
    selected.forEach((id) => onHistory(id));
    toast({ title: 'History refresh requested', description: `${selected.size} sensors`, status: 'info', duration: 2000, position: 'bottom-right' });
  };

  const toggleExpanded = useCallback((deviceId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else {
        next.add(deviceId);
        if (onHistory) onHistory(deviceId);
      }
      return next;
    });
  }, [onHistory]);

  const copyDeviceId = useCallback(async (deviceId) => {
    try {
      await navigator.clipboard.writeText(String(deviceId));
      toast({ title: 'Copied', description: String(deviceId), status: 'success', duration: 1500, position: 'bottom-right' });
    } catch {
      toast({ title: 'Copy failed', description: 'Clipboard is unavailable in this browser.', status: 'warning', duration: 2500, position: 'bottom-right' });
    }
  }, [toast]);

  const clearAllFilters = () => {
    setSearch('');
    setLevelFilter('all');
    setQuickFilter('all');
    setFilterTab(0);
  };

  const resolveExportSource = (scopeArg) => {
    // onClick passes an event when used directly, so anything that isn't a scope string means "all"
    const scope = typeof scopeArg === 'string' ? scopeArg : 'all';
    if (scope === 'filtered') return { scope, list: displayed };
    // --- UPDATE: V5 — registry rows (includes live-only devices)
    if (scope === 'selected') return { scope, list: mergedSensors.filter((s) => selected.has(s.device_id)) };
    return { scope: 'all', list: mergedSensors };
    // --- END UPDATE
  };
  // --- END UPDATE

  // ═══ Export ═══
  // --- UPDATE: optional scope ('all' | 'filtered' | 'selected')
  const exportCSV = (scopeArg) => {
    const { scope, list: exportSource } = resolveExportSource(scopeArg);
  // --- END UPDATE
    debuggerRef.startMark('exportCSV');
    // --- UPDATE: V5 — live state, last heard, AI
    const header = ['Device ID', 'Level', 'Fill %', 'Linked Entity', 'Last Updated', 'Live state', 'Last data received (ISO)', 'Registered', 'AI level', 'Flood risk %', 'AI anomaly'];
    // --- END UPDATE
    // --- UPDATE: export the chosen scope
    const rows = exportSource.map(s => {
    // --- END UPDATE
      const r   = liveReadings[s.device_id] || {};
      const cfg = getLevelConfig(r.level);
      const isAssetLink = s.linked_entity_type === 'asset' && s.linked_entity_id;
      const isPipeLink  = !!linksView[s.device_id] || s.linked_entity_type === 'pipeline';
      const entityLabel = isAssetLink
        ? `Asset #${s.linked_entity_id}`
        : isPipeLink
          ? `Pipeline #${s.linked_entity_id || linksView[s.device_id]}`
          : 'Unassigned';
      // --- UPDATE: V5
      const ai = aiByKey.get(s.__key);
      return [
        s.device_id,
        cfg.label,
        cfg.value,
        entityLabel,
        r.timestamp ? new Date(r.timestamp).toLocaleString() : '—',
        s.liveState || '',
        s.__row?.lastHeardMs ? new Date(s.__row.lastHeardMs).toISOString() : '',
        s.__registered === false ? 'no' : 'yes',
        ai?.level ?? '',
        ai?.flood ? Math.round(ai.flood.probability * 100) : '',
        ai?.anomaly ? ai.anomaly.label : '',
      ];
      // --- END UPDATE
    });

    // --- UPDATE: quote fields — toLocaleString() contains commas, which previously split the row
    const csv  = [header, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');
    // --- END UPDATE
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    // --- UPDATE: scope in filename
    a.href = url; a.download = `water-levels-${scope}-${Date.now()}.csv`; a.click();
    // --- END UPDATE
    debuggerRef.endMark('exportCSV');
    debuggerRef.log('success', 'CSV exported', { rows: rows.length });
    // --- UPDATE
    toast({ title: 'Exported', description: `CSV downloaded (${rows.length} rows)`, status: 'success', duration: 2000, position: 'bottom-right' });
    // --- END UPDATE
  };

  // --- UPDATE: optional scope ('all' | 'filtered' | 'selected')
  const exportJSON = (scopeArg) => {
    const { scope, list: exportSource } = resolveExportSource(scopeArg);
    const data = exportSource.map(s => ({
  // --- END UPDATE
      device_id:          s.device_id,
      name:               s.name,
      reading:            liveReadings[s.device_id],
      pipeline:           linksView[s.device_id],
      linked_entity_id:   s.linked_entity_id,
      linked_entity_type: s.linked_entity_type,
      history:            historyView[s.device_id],
      // --- UPDATE: V5
      live_state:         s.liveState,
      last_heard_at:      s.__row?.lastHeardMs ? new Date(s.__row.lastHeardMs).toISOString() : null,
      registered:         s.__registered !== false,
      ai:                 aiByKey.get(s.__key) || null,
      // --- END UPDATE
    }));
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    // --- UPDATE: scope in filename
    a.href = url; a.download = `water-levels-${scope}-${Date.now()}.json`; a.click();
    // --- END UPDATE
    debuggerRef.log('success', 'JSON exported');
    toast({ title: 'Exported', description: 'JSON downloaded', status: 'success', duration: 2000, position: 'bottom-right' });
  };

  // ═══ Sort chevron ═══ (--- UPDATE: V5 — hoisted to module scope --- END UPDATE)

  // ═══ Empty state ═══
  // --- UPDATE: V5 — registry rows (sensors ∪ live-only devices)
  if (!isLoading && mergedSensors.length === 0) {
  // --- END UPDATE
    return (
      <Box py={16} textAlign="center" color={emptyColor}>
        <MotionBox animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}>
          <Icon as={MdWaterDrop} boxSize={14} mb={4} opacity={0.25} />
        </MotionBox>
        <Text fontWeight="700" fontSize="lg" mb={1}>No water level sensors</Text>
        <Text fontSize="sm">
          Sensors of type{' '}
          <Text as="span" fontFamily="mono" fontSize="xs" px={1} py="1px"
            bg={emptyCodeBg} borderRadius="sm">
            water_level
          </Text>{' '}
          will appear here automatically.
        </Text>
      </Box>
    );
  }

  // ═══ Render ═══
  return (
    <Box>
      {/* --- UPDATE: cards double as level filters */}
      {/* --- UPDATE: V5 — feed state (never changes a sensor's own status) + registry-keyed summary */}
      {mergedSensors.length > 0 && (
        <LiveFeedBanner isConnected={isConnected} counts={registry.counts} lastArrivalMs={lastArrivalMs} now={clockTick} />
      )}
      {!isLoading && <SummaryBar sensors={mergedSensors} readings={liveReadings} activeLevel={levelFilter} onSelectLevel={setLevelFilter} />}
      {mergedSensors.length > 0 && (
        <AiFleetStrip summary={aiSummary} active={quickFilter === 'ai'} onToggle={() => setQuickFilter((q) => (q === 'ai' ? 'all' : 'ai'))} />
      )}
      {/* --- END UPDATE */}
      {/* --- END UPDATE */}

      {/* Online/Offline Tab Controls */}
      <Flex mb={4} justify="space-between" align="center" wrap="wrap" gap={4}>
        <Tabs variant="soft-rounded" colorScheme="cyan" index={filterTab} onChange={(idx) => setFilterTab(idx)}>
          <TabList>
            <Tab fontSize="sm" fontWeight="600">All ({mergedSensors.length})</Tab>
            <Tab fontSize="sm" fontWeight="600" color="green.500">
              <Icon as={MdSensors} mr={1}/> Online ({onlineCount})
            </Tab>
            <Tab fontSize="sm" fontWeight="600" color="gray.500">
              <Icon as={MdSensorsOff} mr={1}/> Offline ({offlineCount})
            </Tab>
          </TabList>
        </Tabs>

        {/* --- UPDATE: quick-filter chips */}
        <Wrap spacing={2} role="group" aria-label="Quick filters">
          {[
            { key: 'alerts',     label: 'Active alerts', icon: MdNotificationsActive, color: 'orange' },
            { key: 'ai',         label: 'AI flagged',    icon: MdAutoAwesome,         color: 'purple' },
            { key: 'unassigned', label: 'Unassigned',    icon: MdUnassigned,          color: 'gray'   },
            { key: 'muted',      label: 'Muted',         icon: MdNotificationsOff,    color: 'gray'   },
          ].map((chip) => {
            const isActive = quickFilter === chip.key;
            const count = quickFilterCounts[chip.key];
            return (
              <WrapItem key={chip.key}>
                <Button
                  size="xs"
                  borderRadius="full"
                  variant={isActive ? 'solid' : 'outline'}
                  colorScheme={chip.color}
                  bg={isActive ? undefined : 'transparent'}
                  _hover={{ bg: isActive ? undefined : chipActiveBg }}
                  leftIcon={<Icon as={chip.icon} />}
                  onClick={() => setQuickFilter(isActive ? 'all' : chip.key)}
                  aria-pressed={isActive}
                  isDisabled={count === 0 && !isActive}
                >
                  {chip.label} ({count})
                </Button>
              </WrapItem>
            );
          })}
        </Wrap>
        {/* --- END UPDATE */}
      </Flex>

      {/* Toolbar */}
      <Flex
        justify="space-between" align="center" mb={4} gap={3} p={3}
        bg={headerBg} borderRadius="xl" borderWidth="1px" borderColor={border}
        flexWrap="wrap"
      >
        <HStack spacing={3} flex={1} flexWrap="wrap">
          <InputGroup size="sm" maxW="210px">
            <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
            <Input
              placeholder="Search sensors…" value={search}
              onChange={e => setSearch(e.target.value)}
              borderRadius="lg" bg={inputBg}
              // --- UPDATE
              ref={searchInputRef}
              aria-label="Search sensors (press / to focus)"
              // --- END UPDATE
            />
            {/* --- UPDATE: shortcut hint */}
            {!search && (
              <Box position="absolute" right={2} top="50%" transform="translateY(-50%)" pointerEvents="none" display={{ base: 'none', md: 'block' }}>
                <Kbd fontSize="9px">/</Kbd>
              </Box>
            )}
            {/* --- END UPDATE */}
          </InputGroup>
          <Select
            size="sm" value={levelFilter}
            onChange={e => setLevelFilter(e.target.value)}
            maxW="135px" borderRadius="lg" bg={inputBg}
          >
            <option value="all">All Levels</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
            <option value="EMPTY">Empty</option>
          </Select>
          <AnimatePresence>
            {/* --- UPDATE: also covers quick filter + tab */}
            {(search || levelFilter !== 'all' || quickFilter !== 'all' || filterTab !== 0) && (
              <MotionBox initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}>
                <Button size="xs" variant="ghost" colorScheme="red" borderRadius="full"
                  leftIcon={<Icon as={MdClose} />}
                  onClick={() => { setSearch(''); setLevelFilter('all'); clearAllFilters(); }}
                >
            {/* --- END UPDATE */}
                  Clear
                </Button>
              </MotionBox>
            )}
          </AnimatePresence>

          {selected.size > 0 && (
            <MotionBox initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
              <Button size="xs" colorScheme="red" variant="outline" borderRadius="full"
                leftIcon={<Icon as={MdDelete} />}
                // --- UPDATE: confirm before an irreversible bulk unlink
                onClick={() => setConfirmUnlink(true)}
                // --- END UPDATE
              >
                Unlink ({selected.size})
              </Button>
            </MotionBox>
          )}

          {/* --- UPDATE: more bulk actions */}
          {selected.size > 0 && (
            <MotionBox initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
              <Menu>
                <MenuButton as={Button} size="xs" variant="outline" colorScheme="cyan" borderRadius="full" rightIcon={<MdExpandMore />}>
                  Bulk actions
                </MenuButton>
                <MenuList zIndex={20} fontSize="sm">
                  <MenuItem icon={<MdNotificationsOff />} onClick={() => bulkSetMuted(true)}>Mute alerts</MenuItem>
                  <MenuItem icon={<MdNotifications />} onClick={() => bulkSetMuted(false)}>Unmute alerts</MenuItem>
                  {onHistory && <MenuItem icon={<MdRefresh />} onClick={bulkRefreshHistory}>Refresh history</MenuItem>}
                  <MenuDivider />
                  <MenuItem icon={<MdFileDownload />} onClick={() => exportCSV('selected')}>Export selected (CSV)</MenuItem>
                  <MenuItem icon={<MdFileDownload />} onClick={() => exportJSON('selected')}>Export selected (JSON)</MenuItem>
                  <MenuDivider />
                  <MenuItem icon={<MdClose />} onClick={() => setSelected(new Set())}>Clear selection</MenuItem>
                </MenuList>
              </Menu>
            </MotionBox>
          )}
          {/* --- END UPDATE */}
        </HStack>

        <HStack spacing={2}>
          <DataQualityIndicator score={dataQuality} />

          {/* --- UPDATE: V5 */}
          <Tooltip label={autoRefresh ? `Auto-refresh history ON (visible rows, every ${AUTO_REFRESH_MS / 1000}s)` : 'Auto-refresh history OFF'} hasArrow>
          {/* --- END UPDATE */}
            <IconButton
              icon={<Icon as={MdAutorenew} className={autoRefresh ? 'spin' : ''} />}
              size="sm" variant="ghost"
              colorScheme={autoRefresh ? 'green' : 'gray'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              aria-label="Auto-refresh"
            />
          </Tooltip>

          {/* --- UPDATE: debugger only exists in development, so only show its button there */}
          {debuggerRef.enabled && (
          <Tooltip label="Debug Panel" hasArrow>
            <IconButton
              icon={<Icon as={MdBugReport} />}
              size="sm" variant="ghost" colorScheme="purple"
              onClick={debugOnOpen}
              aria-label="Debug"
            />
          </Tooltip>
          )}
          {/* --- END UPDATE */}

          {/* --- UPDATE: density + column picker */}
          <Tooltip label={density === 'compact' ? 'Comfortable rows' : 'Compact rows'} hasArrow>
            <IconButton
              icon={<Icon as={MdFormatLineSpacing} />}
              size="sm" variant={density === 'compact' ? 'solid' : 'ghost'} colorScheme="gray"
              onClick={() => setDensity((d) => (d === 'compact' ? 'comfortable' : 'compact'))}
              aria-label="Toggle row density"
            />
          </Tooltip>

          <Menu closeOnSelect={false}>
            <Tooltip label="Columns" hasArrow>
              <MenuButton as={IconButton} icon={<MdViewColumn />} size="sm" variant="ghost" aria-label="Choose columns" />
            </Tooltip>
            <MenuList zIndex={20} minW="200px">
              <MenuOptionGroup
                title="Visible columns"
                type="checkbox"
                value={COLUMN_LABELS.filter((c) => visibleColumns[c.key]).map((c) => c.key)}
                onChange={(vals) => {
                  const list = Array.isArray(vals) ? vals : [vals];
                  if (!list.length) return; // keep at least one column
                  setVisibleColumns(COLUMN_LABELS.reduce((acc, c) => ({ ...acc, [c.key]: list.includes(c.key) }), {}));
                }}
              >
                {COLUMN_LABELS.map((c) => (
                  <MenuItemOption key={c.key} value={c.key} fontSize="sm">{c.label}</MenuItemOption>
                ))}
              </MenuOptionGroup>
              <MenuDivider />
              <MenuItem fontSize="sm" icon={<MdRefresh />} onClick={() => setVisibleColumns({ ...DEFAULT_COLUMNS })}>
                Reset columns
              </MenuItem>
            </MenuList>
          </Menu>
          {/* --- END UPDATE */}

          <Text fontSize="11px" color="gray.400" fontWeight="600">{displayed.length}/{mergedSensors.length}</Text>

          <Menu>
            <MenuButton as={IconButton} icon={<MdDownload />} size="sm" variant="ghost" colorScheme="blue" aria-label="Export" />
            <MenuList>
              <MenuItem icon={<MdFileDownload />} onClick={exportCSV}>Export CSV</MenuItem>
              <MenuItem icon={<MdFileDownload />} onClick={exportJSON}>Export JSON</MenuItem>
              {/* --- UPDATE: filtered / selected scopes */}
              <MenuDivider />
              <MenuGroup title={`Filtered view (${displayed.length})`} fontSize="xs">
                <MenuItem icon={<MdFilterList />} onClick={() => exportCSV('filtered')}>Filtered CSV</MenuItem>
                <MenuItem icon={<MdFilterList />} onClick={() => exportJSON('filtered')}>Filtered JSON</MenuItem>
              </MenuGroup>
              {selected.size > 0 && (
                <MenuGroup title={`Selected (${selected.size})`} fontSize="xs">
                  <MenuItem icon={<MdSelectAll />} onClick={() => exportCSV('selected')}>Selected CSV</MenuItem>
                  <MenuItem icon={<MdSelectAll />} onClick={() => exportJSON('selected')}>Selected JSON</MenuItem>
                </MenuGroup>
              )}
              {/* --- END UPDATE */}
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {/* Table */}
      <Box
        overflowX="auto" borderRadius="xl" borderWidth="1px" borderColor={border}
        // --- UPDATE: optional scroll container (enables the sticky header)
        maxH={maxTableHeight || undefined}
        overflowY={maxTableHeight ? 'auto' : undefined}
        // --- END UPDATE
      >
        <Table
          variant="unstyled" size="sm"
          // --- UPDATE: compact density overrides the per-cell py={3}
          sx={density === 'compact' ? { '& tbody td': { paddingTop: '5px', paddingBottom: '5px' } } : undefined}
          // --- END UPDATE
        >
          <Thead
            // --- UPDATE
            position={maxTableHeight ? 'sticky' : undefined}
            top={0}
            zIndex={2}
            bg={maxTableHeight ? stickyHeadBg : undefined}
            // --- END UPDATE
          >
            <Tr bg={headerBg}>
              {/* Select-all checkbox */}
              <Th w="36px" py={3} px={4}>
                <Checkbox
                  isChecked={selected.size === displayed.length && displayed.length > 0}
                  isIndeterminate={selected.size > 0 && selected.size < displayed.length}
                  onChange={toggleSelectAll}
                  colorScheme="cyan"
                />
              </Th>

              {[
                // --- UPDATE: V5 — live state column
                { key: null,        label: 'Live',           w: '96px',  sort: false, visible: 'status'   },
                // --- END UPDATE
                { key: 'device_id', label: 'Sensor',         w: null,    sort: true,  visible: 'id'       },
                { key: 'level',     label: 'Level',          w: null,    sort: true,  visible: 'level'    },
                { key: null,        label: 'Fill Status',    w: '160px', sort: false, visible: 'fill'     },
                { key: null,        label: 'Trend',          w: '56px',  sort: false, visible: 'trend'    },
                // --- UPDATE: sortable by AI flood risk
                { key: 'ai',        label: 'Alert / FlowSense AI', w: '130px', sort: true,  visible: 'alert'    },
                // --- END UPDATE
                { key: null,        label: 'Linked Entity',  w: null,    sort: false, visible: 'pipeline' },
                { key: 'updated',   label: 'Last Data',      w: '120px', sort: true,  visible: 'lastSeen' },
                { key: null,        label: 'Actions',        w: '100px', sort: false, visible: 'actions'  },
              ].filter(col => visibleColumns[col.visible]).map((col, ci) => (
                <Th
                  key={ci} w={col.w}
                  fontSize="9px" fontWeight="800" color={thColor}
                  letterSpacing="0.09em" textTransform="uppercase"
                  py={3} px={4}
                  cursor={col.sort ? 'pointer' : 'default'}
                  _hover={col.sort ? { color: 'cyan.500' } : {}}
                  onClick={col.sort && col.key ? () => toggleSort(col.key) : undefined}
                  borderBottomWidth="2px" borderBottomColor={border}
                >
                  <HStack spacing={0}>
                    <Text>{col.label}</Text>
                    {col.sort && col.key && <SortChevron col={col.key} sortKey={sortKey} sortDir={sortDir} />}
                  </HStack>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {/* --- UPDATE: V5 — AnimatePresence removed; rows are memoised WaterLevelRow --- END UPDATE */}
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <Tr key={`sk-${i}`}>
                      <Td><Skeleton h="18px" /></Td>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <Td key={j} py={3} px={4}><Skeleton h="18px" borderRadius="md" /></Td>
                      ))}
                    </Tr>
                  ))
                : displayed.length === 0
                ? (
                  <Tr>
                    {/* --- UPDATE: colSpan follows visible columns */}
                    <Td colSpan={columnCount} py={12} textAlign="center" color="gray.400">
                    {/* --- END UPDATE */}
                      <VStack spacing={2}>
                        <Icon as={MdSearch} boxSize={8} />
                        <Text fontSize="sm" fontWeight="600">No sensors match filters</Text>
                        {/* --- UPDATE */}
                        <Button size="xs" variant="outline" colorScheme="cyan" borderRadius="full" onClick={clearAllFilters}>
                          Clear all filters
                        </Button>
                        {/* --- END UPDATE */}
                      </VStack>
                    </Td>
                  </Tr>
                )
                // --- UPDATE: V5 — memoised rows; every prop is a primitive, a cached object or a stable handler
                : pagedDisplayed.map((sensor) => (
                    <WaterLevelRow
                      key={sensor.__key}
                      sensor={sensor}
                      reading={liveReadings[sensor.device_id]}
                      history={historyView[sensor.device_id]}
                      linkedPipelineId={linksView[sensor.device_id]}
                      isMuted={!!muted[sensor.device_id]}
                      isChecked={selected.has(sensor.device_id)}
                      isSelected={selectedKey !== null && selectedKey === sensor.__key}
                      isExpanded={expanded.has(sensor.device_id)}
                      visibleColumns={visibleColumns}
                      columnCount={columnCount}
                      ageLabel={formatAgo(sensor.__row?.lastHeardMs, clockTick)}
                      ai={aiByKey.get(sensor.__key)}
                      thresholds={registry.thresholds}
                      colors={rowColors}
                      canSelect={!!onSensorSelect}
                      onSelect={selectRow}
                      onToggleSelect={toggleSelect}
                      onToggleExpanded={toggleExpanded}
                      onOpenHistory={openHistory}
                      onToggleMute={toggleMute}
                      onCopy={copyDeviceId}
                      onLink={linkRow}
                      onLinkAsset={linkAssetRow}
                      onUnlink={unlinkRow}
                    />
                  ))
                // --- END UPDATE
              }
          </Tbody>
        </Table>
      </Box>

      {/* Footer */}
      {!isLoading && displayed.length > 0 && (
        <Flex justify="space-between" align="center" mt={2} px={1}>
          <HStack spacing={2} fontSize="10px" color="gray.400">
            {selected.size > 0 && (
              <Text fontWeight="600">{selected.size} selected</Text>
            )}
          </HStack>
          {/* --- UPDATE: paging-aware count + shortcut hints */}
          <HStack spacing={3}>
            <HStack spacing={1} fontSize="10px" color="gray.400" display={{ base: 'none', md: 'flex' }}>
              <Kbd fontSize="9px">/</Kbd><Text>search</Text>
              <Kbd fontSize="9px">Esc</Kbd><Text>clear selection</Text>
            </HStack>
            <Text fontSize="10px" color="gray.400">
              Showing {pagedDisplayed.length} of {displayed.length} matching · {mergedSensors.length} total
            </Text>
          </HStack>
          {/* --- END UPDATE */}
        </Flex>
      )}

      {/* --- UPDATE: incremental rendering */}
      {!isLoading && remainingCount > 0 && (
        <Flex justify="center" mt={3}>
          <Button size="sm" variant="outline" colorScheme="cyan" borderRadius="full" leftIcon={<MdExpandMore />}
            onClick={() => setVisibleCount((c) => c + pageSize)}
          >
            Load {Math.min(pageSize, remainingCount)} more ({remainingCount} remaining)
          </Button>
        </Flex>
      )}

      {/* Bulk unlink confirmation */}
      <AlertDialog isOpen={confirmUnlink} leastDestructiveRef={cancelUnlinkRef} onClose={() => setConfirmUnlink(false)} isCentered>
        <AlertDialogOverlay backdropFilter="blur(4px)">
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="md" fontWeight="800">
              Unlink {selected.size} sensor{selected.size === 1 ? '' : 's'}?
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Each selected sensor will be detached from its pipeline or asset. Readings keep arriving, but they will no
              longer feed that entity&apos;s telemetry or AI predictions until re-linked.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelUnlinkRef} size="sm" variant="ghost" onClick={() => setConfirmUnlink(false)}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="red" ml={3} leftIcon={<MdLinkOff />}
                onClick={() => { setConfirmUnlink(false); bulkUnlink(); }}
              >
                Unlink {selected.size}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {/* --- END UPDATE */}

      {/* History Drawer */}
      <HistoryDrawer
        isOpen={historyOpen}
        onClose={historyOnClose}
        deviceId={activeSensor}
        // --- UPDATE: V5 — case-insensitive
        history={historyView[activeSensor] || []}
        // --- END UPDATE
      />

      {/* Debug Panel */}
      <DebugPanel
        debuggerInstance={debuggerRef}
        isOpen={debugOpen}
        onClose={debugOnClose}
      />
    </Box>
  );
};

export default WaterLevelTable;